import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Star,
  Clock,
  ArrowRight,
  FileText,
  Settings,
  CheckSquare,
  Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useUIStore } from '../lib/store';

interface SearchResult {
  id: string;
  title: string;
  icon?: string;
  snippet?: string;
  updatedAt: string;
  isFavorited?: boolean;
}

interface RecentSearch {
  query: string;
  resultCount: number;
  searchedAt: string;
}

export default function CommandPalette() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'en' | 'ar';
  const navigate = useNavigate();
  const { commandPaletteOpen, setCommandPaletteOpen } = useUIStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Quick actions
  const quickActions = [
    { id: 'new-doc', icon: <FileText className="h-4 w-4" />, label: t('document.newPage'), action: () => navigate('/') },
    { id: 'tasks', icon: <CheckSquare className="h-4 w-4" />, label: t('nav.tasks'), action: () => navigate('/tasks') },
    { id: 'settings', icon: <Settings className="h-4 w-4" />, label: t('nav.settings'), action: () => navigate('/settings') },
  ];

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(!commandPaletteOpen);
      }
      if (e.key === 'Escape' && commandPaletteOpen) {
        setCommandPaletteOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [commandPaletteOpen, setCommandPaletteOpen]);

  // Focus input when opened
  useEffect(() => {
    if (commandPaletteOpen) {
      inputRef.current?.focus();
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      fetchRecentSearches();
    }
  }, [commandPaletteOpen]);

  // Fetch recent searches
  const fetchRecentSearches = async () => {
    try {
      const res = await fetch('/api/search/recent');
      if (res.ok) {
        const data = await res.json() as { searches?: RecentSearch[] };
        setRecentSearches(data.searches || []);
      }
    } catch {
      // Ignore errors
    }
  };

  // Search with debounce
  const search = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json() as { results?: SearchResult[] };
        setResults(data.results || []);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => search(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const items = query ? results : quickActions;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (query && results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      } else if (!query && quickActions[selectedIndex]) {
        quickActions[selectedIndex].action();
        setCommandPaletteOpen(false);
      }
    }
  };

  const handleSelect = (result: SearchResult) => {
    navigate(`/app/document/${result.id}`);
    setCommandPaletteOpen(false);
  };

  const handleRecentClick = (searchQuery: string) => {
    setQuery(searchQuery);
  };

  if (!commandPaletteOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in"
        onClick={() => setCommandPaletteOpen(false)}
      />

      {/* Modal */}
      <div className="fixed inset-x-4 top-[20%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[560px] z-50 animate-scale-in">
        <div className="bg-popover rounded-xl shadow-2xl border overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 p-4 border-b">
            <Search className="h-5 w-5 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={lang === 'ar' ? 'ابحث أو اذهب إلى...' : 'Search or jump to...'}
              className="flex-1 bg-transparent border-0 outline-none text-lg placeholder:text-muted-foreground"
              dir="auto"
            />
            {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
            <kbd className="hidden sm:block px-2 py-1 rounded bg-secondary text-xs">
              esc
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto p-2">
            {query ? (
              // Search results
              results.length > 0 ? (
                <div>
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {lang === 'ar' ? 'النتائج' : 'Results'}
                  </div>
                  {results.map((result, i) => (
                    <button
                      key={result.id}
                      onClick={() => handleSelect(result)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-start transition-colors',
                        selectedIndex === i ? 'bg-secondary' : 'hover:bg-secondary/50'
                      )}
                    >
                      <span className="text-xl">{result.icon || '📄'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{result.title}</div>
                        {result.snippet && (
                          <div
                            className="text-sm text-muted-foreground truncate"
                            dangerouslySetInnerHTML={{ __html: result.snippet }}
                          />
                        )}
                      </div>
                      {result.isFavorited && (
                        <Star className="h-4 w-4 text-yellow-500 fill-current shrink-0" />
                      )}
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 flip-rtl" />
                    </button>
                  ))}
                </div>
              ) : !loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  {lang === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                </div>
              ) : null
            ) : (
              // Quick actions and recent searches
              <>
                {/* Quick Actions */}
                <div className="mb-4">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {lang === 'ar' ? 'إجراءات سريعة' : 'Quick Actions'}
                  </div>
                  {quickActions.map((action, i) => (
                    <button
                      key={action.id}
                      onClick={() => {
                        action.action();
                        setCommandPaletteOpen(false);
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                        selectedIndex === i ? 'bg-secondary' : 'hover:bg-secondary/50'
                      )}
                    >
                      {action.icon}
                      <span>{action.label}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground ms-auto flip-rtl" />
                    </button>
                  ))}
                </div>

                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <div>
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center justify-between">
                      <span>{lang === 'ar' ? 'عمليات البحث الأخيرة' : 'Recent Searches'}</span>
                      <button
                        onClick={async () => {
                          await fetch('/api/search/recent', { method: 'DELETE' });
                          setRecentSearches([]);
                        }}
                        className="text-xs hover:text-primary"
                      >
                        {lang === 'ar' ? 'مسح' : 'Clear'}
                      </button>
                    </div>
                    {recentSearches.slice(0, 5).map((search) => (
                      <button
                        key={search.searchedAt}
                        onClick={() => handleRecentClick(search.query)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                      >
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1 text-start truncate">{search.query}</span>
                        <span className="text-xs text-muted-foreground">
                          {search.resultCount} {lang === 'ar' ? 'نتيجة' : 'results'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t bg-secondary/30 text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-secondary">↑↓</kbd>
                {lang === 'ar' ? 'تنقل' : 'Navigate'}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-secondary">↵</kbd>
                {lang === 'ar' ? 'فتح' : 'Open'}
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-secondary">⌘K</kbd>
              {lang === 'ar' ? 'لوحة الأوامر' : 'Command palette'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
